import { sentryDsn } from '@/application/constants/env-variables';
import { RootStore } from '@/application/redux-store';
import { logoutAction } from '@/application/redux-store/root-reducer';
import { Loading } from '@/components/uikit/suspense/Loading';
import { sendRefreshTokenRequest } from '@/helpers/interceptors/auth/auth-client';
import {
  addLogoutHandler,
  setupAuthInterceptor,
  useIsAuthorized,
} from '@/helpers/interceptors/auth/auth-interceptor';
import { injectLanguageInterceptor } from '@/helpers/interceptors/inject-language-interceptor';
import { sessionAxiosInterceptor } from '@/helpers/interceptors/inject-session-interceptor';
import { backendUri } from '@/helpers/openid/openid-settings';
import { QuerySuspenseErrorWrapper } from '@/helpers/retry-helper';
import { useDonationCards } from '@/hooks/useDonationCards';
import { authorizedRoutes } from '@/pages/authorized/routes';
import { anonymousRoutes } from '@/pages/unauthorized/routes';
import { QueryFactory } from '@/services/api';
import { ValidationProblemDetails } from '@/services/api/api-client';
import { preloadCalculator } from '@/services/calculator/calculatorWasm';
import CheckBoxOutlineBlankSharpIcon from '@mui/icons-material/CheckBoxOutlineBlankSharp';
import CheckBoxSharpIcon from '@mui/icons-material/CheckBoxSharp';
import IndeterminateCheckBoxSharpIcon from '@mui/icons-material/IndeterminateCheckBoxSharp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Suspense, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router/dom';
import { PersistGate } from 'redux-persist/integration/react';
import { useLocalStorage } from 'usehooks-ts';

QueryFactory.setAxiosFactory(() => axios);

setupAuthInterceptor(axios, (authData) => sendRefreshTokenRequest(authData.refresh_token));
addLogoutHandler(() => {
  RootStore.store.dispatch(logoutAction());
  window.history.pushState(null, '', backendUri);
});
axios.interceptors.request.use(injectLanguageInterceptor);
axios.interceptors.request.use(sessionAxiosInterceptor);

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: sentryDsn(),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });
}

export const App = () => {
  const isAuth = useIsAuthorized();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Warm up the WASM engine as soon as the user is authorized.
  // This downloads and instantiates the multi-MB .NET runtime in the background so
  // the first on-the-fly calculation is instant. Idempotent, so re-runs are harmless.
  useEffect(() => {
    if (isAuth) {
      preloadCalculator();
    }
  }, [isAuth]);

  const { setMaximumPhotosUploadCountReached } = useDonationCards();

  const theme = useMemo(() => {
    return createTheme({
      typography: {
        subtitle2: {
          fontWeight: 300,
          fontSize: '0.75rem',
          lineHeight: '1.25rem',
        },
        body1: {
          fontWeight: 400,
          fontSize: '1rem',
          lineHeight: '1.5rem',
        },
        body2: {
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
        },
        h1: {
          fontWeight: 600,
          fontSize: '2rem',
          lineHeight: '2rem',
          color: '#2f80ed',
        },
        h2: {
          fontWeight: 600,
          fontSize: '0.95rem',
        },
        fontFamily: 'IBM Plex Sans',
      },
      palette: {
        primary: {
          main: '#2f80ed',
        },
        error: {
          main: '#d32f2f',
        },
      },
      components: {
        // One arrow shape for everything that opens a list: the number fields' chevron, in place of
        // MUI's filled triangle. Only the glyph changes, so existing sizing rules keep working.
        MuiAutocomplete: {
          defaultProps: {
            popupIcon: <KeyboardArrowDownIcon />,
          },
        },
        MuiSelect: {
          defaultProps: {
            IconComponent: KeyboardArrowDownIcon,
          },
        },
        MuiCheckbox: {
          // Square corners, like the rest of the app's controls; MUI's own glyphs are rounded.
          defaultProps: {
            icon: <CheckBoxOutlineBlankSharpIcon />,
            checkedIcon: <CheckBoxSharpIcon />,
            indeterminateIcon: <IndeterminateCheckBoxSharpIcon />,
          },
          styleOverrides: {
            // The palette's mid gray ($resources-text-gray) rather than MUI's darker text.secondary.
            // Checked, disabled and indeterminate keep MUI's own colours, set at a higher specificity.
            root: {
              color: '#828282',
            },
          },
        },
      },
    });
  }, []);

  const queryClient = useMemo(() => {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          throwOnError: true,
        },
        mutations: {
          onError: (error) => {
            if ((error as unknown as ValidationProblemDetails)?.detail) {
              const errorMessage: string =
                (error as unknown as ValidationProblemDetails).detail ?? '';
              if (errorMessage === 'ValidationErrors.MaximumPhotosUploadCountReached') {
                setMaximumPhotosUploadCountReached(true);
                return;
              }
              enqueueSnackbar(t(errorMessage), { variant: 'error' });
            }
            enqueueSnackbar(t('InternalErrors.UnexpectedError'), { variant: 'error' });
          },
        },
      },
    });
    addLogoutHandler(() => {
      queryClient.clear();
    });
    return queryClient;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fallback = useMemo(() => {
    return <Loading loading={true} />;
  }, []);

  // RouterProvider keeps rendering the state of the router it mounted with,
  // so swapping the router instance on login/logout requires a remount (key)
  const router = useMemo(() => (isAuth ? authorizedRoutes() : anonymousRoutes()), [isAuth]);

  return (
    <Suspense fallback={fallback}>
      <QueryClientProvider client={queryClient}>
        <Provider store={RootStore.store}>
          <PersistGate loading={fallback} persistor={RootStore.persistor}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <QuerySuspenseErrorWrapper>
                  <RouterProvider key={isAuth ? 'authorized' : 'anonymous'} router={router} />
                </QuerySuspenseErrorWrapper>
              </ThemeProvider>
            </StyledEngineProvider>
          </PersistGate>
        </Provider>
      </QueryClientProvider>
    </Suspense>
  );
};
